/**
 * File-History-Store
 *
 * Clientseitige, dauerhafte Dateiverwaltung mit Verlaufshistorie. Metadaten **und** Bytes liegen
 * in IndexedDB (via Dexie, siehe {@link @/services/historyDb}) – getrennt in zwei Tabellen, damit
 * der reaktive State leichtgewichtig bleibt und Blobs erst bei Bedarf geladen werden. Übersteht
 * Reload; der Server ist stateless und speichert ohnehin nichts (einzige dauerhafte Persistenz).
 *
 * Befüllt wird der Store automatisch über das Composable
 * {@link @/composables/useHistoryCapture} (Watcher auf den Upload-Store). Umgekehrt importiert der
 * Upload-Store diesen Store NICHT (kein Zirkelbezug, daher statischer Import möglich); in dieser
 * Richtung greifen mehrere Aktionen (`reuse`, `addUpload`/`addDerived` via Zeiger-Updates,
 * `loadPreviousState`, `switchMovement`, `restoreCurrentFromHistory`) auf den Upload-Store zu.
 */

import {computed, ref} from 'vue';
import {downloadBlob} from '@/services/download';
import {defineStore} from 'pinia';
import {type AnalysisSummary, historyDb, type HistoryEntry, type HistoryKind,} from '@/services/historyDb';
import {hashBlob} from '@/services/fileIdentity';
import {chordInferenceStatus} from '@/composables/musicXmlDom';
import {stableBySignature} from '@/stores/historyMemo';
import type {AnalysisResult, ConversionSettings} from '@/generated';
import {useNotificationStore} from '@/stores/notifications';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import {useSettingsStore} from '@/stores/settings';

/** Maximale Anzahl an Root-Einträgen (Originaldateien); ältester wird verdrängt. */
const MAX_ROOTS = 50;
/** Ab diesem Füllgrad des Origin-Speichers wird vor dem Schreiben verdrängt. */
const QUOTA_EVICT_THRESHOLD = 0.9;

const MUSICXML_MIME = 'application/vnd.recordare.musicxml+xml';

function indexedDbAvailable(): boolean {
    return typeof indexedDB !== 'undefined';
}

function newId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    return `entry-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Leichte Zusammenfassung aus einer Analyse-Response für die Anzeige. */
function toSummary(analysis: AnalysisResult): AnalysisSummary {
    return {
        key: analysis.key ?? null,
        timeSignature: analysis.time_signature ?? null,
        numMeasures: analysis.num_measures,
        numNotes: analysis.num_notes,
        unmappableCount: analysis.unmappable_count,
    };
}

/**
 * Wandelt ein (potenziell reaktives Vue-Proxy-)Objekt in ein reines, strukturiert klonbares
 * Plain-Object um. IndexedDB kann reaktive Proxies nicht per Structured-Clone speichern
 * (DataCloneError), daher hier zwingend vor jedem `put` anwenden.
 */
function toPlain<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}

interface AddUploadInput {
    serverFileId: string;
    filename: string;
    fileType?: string | null;
    /** Rohe hochgeladene Datei (Standard-Uploads: MusicXML/MIDI). */
    file?: File;
    /**
     * MusicXML-Ergebnis, das als Bytes gespeichert wird. Für PDF-Uploads: das (teure)
     * Audiveris/OMR-Resultat – so überspringt die Wiederverwendung die lange Konvertierung.
     */
    musicXml?: string;
    /** Gruppierung mehrerer Movements eines mehrteiligen PDFs (gemeinsame documentId). */
    documentId?: string | null;
    /** 0-basierter Movement-Index im Dokument. */
    movementIndex?: number | null;
    /**
     * Wenn ``false``, wird der ``currentEntryId``-Zeiger NICHT auf diesen Eintrag gesetzt
     * (für nicht-aktive Movements eines Dokuments). Standard ``true``.
     */
    makeCurrent?: boolean;
    /**
     * Wenn ``true``, wird die „Upload-Merge"-Duplikaterkennung (Wiederverwendung einer bestehenden
     * Linie bei inhaltsgleichem Upload) übersprungen – für Movement-Uploads, die stets eine eigene
     * frische Linie bekommen sollen. Standard ``false``.
     */
    skipDedup?: boolean;
}

interface AddConvertedInput {
    serverFileId: string;
    filename: string;
    musicXml: string;
    conversionId?: string | null;
    semitones?: number;
    analysis?: AnalysisResult | null;
    settings?: ConversionSettings | null;
}

export interface HistoryGroup {
    /** Root-Eintrag (kind 'upload') oder ältester Eintrag der Gruppe. */
    root: HistoryEntry;
    /** Abgeleitete Einträge (transposed/converted), neueste zuerst. */
    children: HistoryEntry[];
}

export const useFileHistoryStore = defineStore('fileHistory', () => {
    const notificationStore = useNotificationStore();

    const entries = ref<HistoryEntry[]>([]);
    const isSupported = ref(indexedDbAvailable());
    const isHydrated = ref(false);
    let persistenceRequested = false;
    let captureErrorNotified = false;

    // Cross-Tab-Sync: andere Tabs nach Schreibvorgängen den reaktiven State auffrischen lassen.
    const broadcast =
        typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('music-history') : null;
    if (broadcast) broadcast.onmessage = () => void refresh();

    function notifyChange(): void {
        broadcast?.postMessage('changed');
    }

    /** Liest die Metadaten neu aus IndexedDB (z. B. nach Änderungen in einem anderen Tab). */
    async function refresh(): Promise<void> {
        if (!isSupported.value) return;
        try {
            entries.value = await historyDb.entries.toArray();
        } catch {
            /* ignore */
        }
    }
    /** Id des aktuell im Detail-Dialog geöffneten Eintrags (UI-State). */
    const selectedId = ref<string | null>(null);

    // ============ Computed ============

    /** Nach Originaldatei (sourceFileId) gruppiert; Gruppen nach jüngster Aktivität (neueste zuerst). */
    const groups = computed<HistoryGroup[]>(
        stableBySignature(
            () => {
                const bySource = new Map<string, HistoryEntry[]>();
                for (const entry of entries.value) {
                    const arr = bySource.get(entry.sourceFileId) ?? [];
                    arr.push(entry);
                    bySource.set(entry.sourceFileId, arr);
                }

                const built: HistoryGroup[] = [];
                for (const items of bySource.values()) {
                    const sortedAsc = [...items].sort((a, b) => a.createdAt - b.createdAt);
                    const root = sortedAsc.find((e) => e.kind === 'upload') ?? sortedAsc[0];
                    if (!root) continue;
                    const children = sortedAsc
                        .filter((e) => e.id !== root.id)
                        .sort((a, b) => b.createdAt - a.createdAt);
                    built.push({root, children});
                }
                // Gruppen nach jüngster Aktivität sortieren (neueste zuerst): eine Gruppe mit einem
                // frischen Edit/Transponieren/Konvertieren rückt nach oben, nicht nach Original-Uploadzeit.
                const lastActivity = (g: HistoryGroup) =>
                    Math.max(g.root.createdAt, ...g.children.map((c) => c.createdAt));
                built.sort((a, b) => lastActivity(b) - lastActivity(a));
                return built;
            },
            (built) =>
                built
                    .map((g) => `${g.root.id}:${g.children.map((c) => c.id).join('|')}`)
                    .join(','),
        ),
    );

    const isEmpty = computed(() => entries.value.length === 0);

    /** Aktuell ausgewählter Eintrag (für den Detail-Dialog). */
    const selectedEntry = computed<HistoryEntry | null>((oldValue) => {
        const found = entries.value.find((e) => e.id === selectedId.value) ?? null;
        if (oldValue && found && oldValue.id === found.id) return oldValue;
        return found;
    });

    // ============ Helpers ============

    /** Der Root-Upload-Eintrag einer Gruppe (gemeinsamer Kern von rootIdFor/rootEntryFor). */
    function rootUploadFor(sourceFileId: string): HistoryEntry | null {
        return entries.value.find(
            (e) => e.sourceFileId === sourceFileId && e.kind === 'upload',
        ) ?? null;
    }

    function rootIdFor(serverFileId: string): string | null {
        return rootUploadFor(serverFileId)?.id ?? null;
    }

    /** Findet den Root-Upload-Eintrag einer Gruppe (für Original-Bytes beim Reuse). */
    function rootEntryFor(entry: HistoryEntry): HistoryEntry {
        return rootUploadFor(entry.sourceFileId) ?? entry;
    }

    /** Liest die Bytes eines Eintrags aus IndexedDB (lazy, nicht im reaktiven State gehalten). */
    async function getBlob(id: string): Promise<Blob | null> {
        try {
            const record = await historyDb.blobs.get(id);
            return record?.blob ?? null;
        } catch (error) {
            console.warn('Dateihistorie: Blob konnte nicht gelesen werden.', error);
            return null;
        }
    }

    async function persist(
        entry: HistoryEntry,
        blob: Blob,
        analysis: AnalysisResult | null = null,
    ): Promise<boolean> {
        if (!isSupported.value) return false;

        // Content-Hash berechnen (falls nicht schon gesetzt) und exakte Dubletten innerhalb
        // derselben Bearbeitungslinie vermeiden (z. B. Rück-Transposition, No-op-Edit).
        if (!entry.contentHash) {
            entry.contentHash = (await hashBlob(blob)) ?? undefined;
        }
        if (
            entry.contentHash &&
            entries.value.some(
                (e) => e.sourceFileId === entry.sourceFileId && e.contentHash === entry.contentHash,
            )
        ) {
            return false;
        }

        try {
            void requestPersistence();
            await evictForQuota();
            await historyDb.transaction(
                'rw',
                historyDb.entries,
                historyDb.blobs,
                historyDb.analyses,
                async () => {
                    await historyDb.entries.put(toPlain(entry));
                    await historyDb.blobs.put({id: entry.id, blob});
                    if (analysis) {
                        await historyDb.analyses.put({id: entry.id, analysis: toPlain(analysis)});
                    }
                },
            );
            entries.value = [...entries.value, entry];
            notifyChange();
            await enforceRootLimit();
            return true;
        } catch (error) {
            // Capture darf die Pipeline nie blockieren – nur protokollieren + einmalig hinweisen.
            console.warn('Dateihistorie: Eintrag konnte nicht gespeichert werden.', error);
            if (!captureErrorNotified) {
                captureErrorNotified = true;
                notificationStore.notify(
                    'Verlauf konnte nicht gespeichert werden (lokaler Speicher voll oder gesperrt).',
                    'warning',
                    4000,
                );
            }
            return false;
        }
    }

    /** Fordert (einmalig) dauerhaften Speicher an, damit der Browser die History nicht verwirft. */
    async function requestPersistence(): Promise<void> {
        if (persistenceRequested) return;
        persistenceRequested = true;
        try {
            if (navigator.storage?.persist && !(await navigator.storage.persisted?.())) {
                await navigator.storage.persist();
            }
        } catch {
            /* best effort */
        }
    }

    async function evictForQuota(): Promise<void> {
        if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return;

        // Schleifend den jeweils ältesten Root verdrängen, bis wieder unter der Schwelle
        // (ein einzelner Eintrag reicht bei großen Dateien evtl. nicht).
        let notified = false;
        for (let guard = 0; guard < MAX_ROOTS; guard++) {
            const {usage = 0, quota = 0} = await navigator.storage.estimate();
            if (quota <= 0 || usage / quota < QUOTA_EVICT_THRESHOLD) break;

            const oldestRoot = [...entries.value]
                .filter((e) => e.kind === 'upload')
                .sort((a, b) => a.createdAt - b.createdAt)[0];
            if (!oldestRoot) break;

            await removeInternal(oldestRoot.id);
            if (!notified) {
                notified = true;
                notificationStore.notify(
                    'Speicher fast voll – ältere Verlaufseinträge wurden entfernt.',
                    'info',
                    3000,
                );
            }
        }
    }

    async function enforceRootLimit(): Promise<void> {
        const roots = entries.value
            .filter((e) => e.kind === 'upload')
            .sort((a, b) => a.createdAt - b.createdAt);
        let excess = roots.length - MAX_ROOTS;
        for (let i = 0; i < roots.length && excess > 0; i++, excess--) {
            const root = roots[i];
            if (root) await removeInternal(root.id);
        }
    }

    // ============ Capture-Actions (vom Composable aufgerufen) ============

    async function addUpload({
        file,
        musicXml,
        serverFileId,
        filename,
        fileType,
        documentId = null,
        movementIndex = null,
        makeCurrent = true,
        skipDedup = false,
    }: AddUploadInput): Promise<string | null> {
        if (!isSupported.value) return null;
        // Doppelte Erfassung desselben Server-Uploads vermeiden.
        const alreadyCaptured = entries.value.find(
            (e) => e.sourceFileId === serverFileId && e.kind === 'upload',
        );
        if (alreadyCaptured) return alreadyCaptured.id;
        // Bytes: bevorzugt das OMR-MusicXML (PDF), sonst die rohe Datei.
        const blob =
            musicXml != null ? new Blob([musicXml], {type: MUSICXML_MIME}) : file;
        if (!blob) return null;

        const contentHash = (await hashBlob(blob)) ?? undefined;

        // Upload-Merge: ist exakt derselbe Inhalt bereits als Upload vorhanden, KEINE neue Gruppe
        // anlegen, sondern die bestehende Bearbeitungslinie übernehmen (Duplikat-Vermeidung).
        // Für Movement-Uploads übersprungen (jedes Movement bekommt seine eigene frische Linie).
        if (contentHash && !skipDedup) {
            const existing = entries.value.find(
                (e) => e.kind === 'upload' && e.contentHash === contentHash,
            );
            if (existing) {
                const store = useMusicUploadStore();
                store.setLineageId(existing.sourceFileId);
                store.setCurrentEntryId(existing.id);
                notificationStore.notify(
                    'Diese Datei ist bereits im Verlauf – der vorhandene Eintrag wird verwendet.',
                    'info',
                    2500,
                );
                return existing.id;
            }
        }

        const id = newId();
        const entry: HistoryEntry = {
            id,
            kind: 'upload',
            filename,
            mimeType: musicXml != null ? MUSICXML_MIME : file?.type || MUSICXML_MIME,
            fileType: fileType ?? null,
            size: blob.size,
            createdAt: Date.now(),
            parentId: null,
            sourceFileId: serverFileId,
            transposedSemitones: 0,
            contentHash,
            documentId,
            movementIndex,
            // Zustand der Akkorderkennung aus genau dem Inhalt ableiten, der als Blob abgelegt wird.
            chordInference: musicXml != null ? chordInferenceStatus(musicXml) : null,
        };
        const ok = await persist(entry, blob);
        // Root wird zum aktuellen Stand (Start der Kette) – außer bei nicht-aktiven Movements.
        if (ok && makeCurrent) useMusicUploadStore().setCurrentEntryId(id);
        return ok ? id : null;
    }

    async function addConverted({
        serverFileId,
        filename,
        musicXml,
        conversionId,
        semitones,
        analysis,
        settings,
    }: AddConvertedInput): Promise<string | null> {
        if (!isSupported.value || !musicXml) return null;
        return addDerived('converted', {
            serverFileId,
            filename,
            musicXml,
            semitones: semitones ?? 0,
            conversionId,
            analysis,
            settings,
        });
    }

    /** Abgeleiteter Stand (transposed/edited/shifted/merged/converted) als Variante in der
     *  Gruppe – die früheren Einzel-Wrapper `addTransposed`/`addEdited`/`addShifted`/`addMerged`
     *  waren identische Delegationen und sind entfallen (useHistoryCapture ruft direkt hier an). */
    async function addDerived(
        kind: HistoryKind,
        opts: {
            serverFileId: string;
            filename: string;
            musicXml: string;
            semitones: number;
            fileType?: string | null;
            conversionId?: string | null;
            analysis?: AnalysisResult | null;
            settings?: ConversionSettings | null;
        },
    ): Promise<string | null> {
        if (!isSupported.value || !opts.musicXml) return null;
        const id = newId();
        const blob = new Blob([opts.musicXml], {type: MUSICXML_MIME});
        const uploadStore = useMusicUploadStore();
        const entry: HistoryEntry = {
            id,
            kind,
            filename: opts.filename,
            mimeType: MUSICXML_MIME,
            fileType: opts.fileType ?? '.musicxml',
            size: blob.size,
            createdAt: Date.now(),
            // Lineare Verkettung: Eltern ist der aktuell geladene Stand (nicht pauschal der Root),
            // damit "Schritt zurück" die echte Bearbeitungsfolge entlangläuft – auch nach Verzweigungen.
            parentId: uploadStore.currentEntryId ?? rootIdFor(opts.serverFileId),
            sourceFileId: opts.serverFileId,
            transposedSemitones: opts.semitones,
            conversionId: opts.conversionId ?? null,
            analysisSummary: opts.analysis ? toSummary(opts.analysis) : null,
            // Settings als reines Plain-Object ablegen (reaktive Proxies → DataCloneError).
            settings: opts.settings ? toPlain(opts.settings) : null,
            // Zustand der Akkorderkennung aus genau dem Inhalt ableiten, der als Blob abgelegt wird.
            chordInference: chordInferenceStatus(opts.musicXml),
        };
        const ok = await persist(entry, blob, opts.analysis ?? null);

        // Den Zeiger auf den neuen Stand setzen – außer bei 'converted' (das ist ein Ausgabe-
        // Artefakt, kein Eingabe-Stand und damit nicht Teil der Undo-Kette).
        if (ok) {
            if (kind !== 'converted') uploadStore.setCurrentEntryId(id);
            return id;
        }
        // Dedup (inhaltsgleich, z. B. Rück-Transposition): der bereits vorhandene,
        // inhaltsgleiche Eintrag wird zum aktuellen Stand.
        const existing = entries.value.find(
            (e) =>
                e.sourceFileId === opts.serverFileId &&
                e.contentHash === entry.contentHash &&
                e.id !== id,
        );
        if (existing && kind !== 'converted') uploadStore.setCurrentEntryId(existing.id);
        return existing?.id ?? null;
    }

    /**
     * Hängt eine Analyse-Response an den passenden, bereits erfassten Eintrag an. Bevorzugt wird
     * der explizit übergebene ``entryId`` (der Zeiger auf den gerade erfassten Stand – die
     * Capture-Queue in useHistoryCapture verarbeitet Events sequentiell, der Eintrag ist also
     * bereits persistiert; das frühere 10×50-ms-Polling ist damit entfallen). Fallback: jüngster
     * Eintrag derselben Linie mit gleichen Transpositions-Halbtönen.
     */
    async function attachAnalysis(
        serverFileId: string,
        semitones: number,
        analysis: AnalysisResult,
        entryId?: string,
    ): Promise<void> {
        if (!isSupported.value || !serverFileId) return;

        const byId = entryId
            ? entries.value.find(
                  (e) => e.id === entryId && e.sourceFileId === serverFileId && e.kind !== 'converted',
              )
            : undefined;
        const target =
            byId ??
            entries.value
                .filter(
                    (e) =>
                        e.sourceFileId === serverFileId &&
                        e.kind !== 'converted' &&
                        (e.transposedSemitones ?? 0) === semitones,
                )
                .sort((a, b) => b.createdAt - a.createdAt)[0];
        if (!target) return;

        const updated: HistoryEntry = {...target, analysisSummary: toSummary(analysis)};
        try {
            await historyDb.transaction('rw', historyDb.entries, historyDb.analyses, async () => {
                // `updated` entsteht per Spread aus einem reaktiven `entries.value`-Eintrag –
                // verschachtelte Objekte (v. a. `settings`) bleiben dabei Vue-Proxies, die
                // IndexedDBs Structured Clone nicht klonen kann (DataCloneError).
                await historyDb.entries.put(toPlain(updated));
                await historyDb.analyses.put({id: updated.id, analysis: toPlain(analysis)});
            });
            entries.value = entries.value.map((e) => (e.id === updated.id ? updated : e));
            notifyChange();
        } catch (error) {
            console.warn('Dateihistorie: Analyse konnte nicht gespeichert werden.', error);
        }
    }

    /** Liest die vollständige (gespeicherte) Analyse eines Eintrags. */
    /** Liest die gespeicherten Bytes als Text (z. B. MusicXML für die Vorschau). */
    async function getText(id: string): Promise<string | null> {
        const blob = await getBlob(id);
        if (!blob) return null;
        try {
            return await blob.text();
        } catch {
            return null;
        }
    }

    // ============ Detail-Auswahl (UI) ============

    function select(id: string): void {
        selectedId.value = id;
    }

    function closeDetail(): void {
        selectedId.value = null;
    }

    // ============ Management-Actions ============

    async function hydrate(): Promise<void> {
        if (isHydrated.value || !isSupported.value) {
            isHydrated.value = true;
            return;
        }
        try {
            // Nur Metadaten in den reaktiven State – Blobs bleiben in IndexedDB.
            entries.value = await historyDb.entries.toArray();
        } catch (error) {
            console.warn('Dateihistorie: Hydration fehlgeschlagen.', error);
            entries.value = [];
            isSupported.value = false;
        } finally {
            isHydrated.value = true;
        }
    }

    /** Interne Löschlogik ohne Notification (für Eviction). */
    async function removeInternal(id: string): Promise<void> {
        const entry = entries.value.find((e) => e.id === id);
        if (!entry) return;

        // Root → Gruppe kaskadierend löschen.
        const toDelete =
            entry.kind === 'upload'
                ? entries.value.filter((e) => e.sourceFileId === entry.sourceFileId)
                : [entry];
        const ids = toDelete.map((e) => e.id);

        try {
            await historyDb.transaction(
                'rw',
                historyDb.entries,
                historyDb.blobs,
                historyDb.analyses,
                async () => {
                    await historyDb.entries.bulkDelete(ids);
                    await historyDb.blobs.bulkDelete(ids);
                    await historyDb.analyses.bulkDelete(ids);
                },
            );
        } catch (error) {
            console.warn('Dateihistorie: Löschen fehlgeschlagen.', error);
        }
        const deletedIds = new Set(ids);
        entries.value = entries.value.filter((e) => !deletedIds.has(e.id));
        notifyChange();
    }

    async function remove(id: string): Promise<void> {
        await removeInternal(id);
        notificationStore.notify('Eintrag aus dem Verlauf entfernt.', 'success', 1500);
    }

    /** Vergibt einen Anzeigenamen für einen Eintrag (leer → zurück auf Dateiname). */
    async function rename(id: string, name: string): Promise<void> {
        const entry = entries.value.find((e) => e.id === id);
        if (!entry) return;
        const trimmed = name.trim();
        const updated: HistoryEntry = {...entry, name: trimmed || null};
        try {
            await historyDb.entries.put(toPlain(updated));
            entries.value = entries.value.map((e) => (e.id === id ? updated : e));
            notifyChange();
        } catch (error) {
            console.warn('Dateihistorie: Umbenennen fehlgeschlagen.', error);
        }
    }

    async function clearAll(): Promise<void> {
        try {
            await historyDb.transaction(
                'rw',
                historyDb.entries,
                historyDb.blobs,
                historyDb.analyses,
                async () => {
                    await historyDb.entries.clear();
                    await historyDb.blobs.clear();
                    await historyDb.analyses.clear();
                },
            );
            entries.value = [];
            notifyChange();
            notificationStore.notify('Verlauf geleert.', 'success', 1500);
        } catch (error) {
            console.warn('Dateihistorie: Leeren fehlgeschlagen.', error);
            notificationStore.notify('Verlauf konnte nicht vollständig geleert werden.', 'error');
        }
    }

    // ============ Reuse ============

    /**
     * Lädt einen Eintrag erneut in den Editor. Da Server-`file_id`s nach ~24h ablaufen,
     * werden die Original-Bytes frisch zum Backend hochgeladen (neue file_id) und – bei
     * transponierten/konvertierten Einträgen – die Transposition reproduziert.
     */
    async function reuse(entry: HistoryEntry): Promise<void> {
        if (!isSupported.value) {
            notificationStore.notify('Verlauf in diesem Browser nicht verfügbar.', 'warning');
            return;
        }

        const uploadStore = useMusicUploadStore();

        // Gespeicherte Konvertierungs-Settings wiederherstellen (sofern am Eintrag vorhanden).
        if (entry.settings) {
            await useSettingsStore().updateSettings({...entry.settings});
        }

        // 'upload'/'edited'/'shifted'/'merged' sind in sich abgeschlossen → eigene Bytes laden
        // (Edit/Shift/Merge enthalten bereits den fertigen Inhalt). 'transposed'/'converted' →
        // Originalbytes + Re-Transponieren.
        const useOwnBytes = entry.kind === 'upload' || entry.kind === 'edited' || entry.kind === 'shifted' || entry.kind === 'merged';
        const sourceEntry = useOwnBytes ? entry : rootEntryFor(entry);
        const bytes = (await getBlob(sourceEntry.id)) ?? (await getBlob(entry.id));
        if (!bytes) {
            notificationStore.notify('Datei konnte nicht aus dem Verlauf geladen werden.', 'error');
            return;
        }

        const file = new File([bytes], sourceEntry.filename, {type: sourceEntry.mimeType});

        uploadStore.setTransposedSemitones(0);
        const {upload} = await uploadStore.updateAndAnalyzeFile(file);
        if (!upload) return;

        const semitones = entry.transposedSemitones ?? 0;
        if (!useOwnBytes && semitones !== 0) {
            try {
                await uploadStore.transposeScore(upload.file_id, semitones);
                await uploadStore.analyzeSelected();
            } catch (error) {
                // Re-Transposition kann fehlschlagen (Server/Netz) – Original bleibt geladen.
                console.warn('Dateihistorie: Re-Transposition beim Reuse fehlgeschlagen.', error);
                notificationStore.notify(
                    'Transposition konnte nicht reproduziert werden – Original wurde geladen.',
                    'warning',
                    4000,
                );
                return;
            }
        }

        notificationStore.notify(`"${sourceEntry.filename}" in den Editor geladen.`, 'success', 2000);
    }

    /** Lädt das gespeicherte Artefakt direkt herunter – ohne Backend. */
    async function download(entry: HistoryEntry): Promise<void> {
        const bytes = await getBlob(entry.id);
        if (!bytes) {
            notificationStore.notify('Datei nicht mehr im Verlauf vorhanden.', 'error');
            return;
        }
        downloadBlob(bytes, entry.filename);
        notificationStore.notify('Download gestartet.', 'success', 1500);
    }

    /**
     * Öffnet den Eintrag in einem NEUEN Browser-Tab. Der neue Tab lädt die App mit `?reuse=<id>`;
     * da IndexedDB tab-übergreifend geteilt ist, liest er den Eintrag dort und reused ihn
     * (siehe {@link consumeReuseParam}). So bleiben beide Tabs unabhängig (eigener URL-State).
     */
    function openInNewTab(entry: HistoryEntry): void {
        if (typeof window === 'undefined') return;
        window.open(`${location.pathname}?reuse=${encodeURIComponent(entry.id)}`, '_blank');
    }

    /**
     * Lädt den Eintrag mit der angegebenen Id aus IndexedDB und reused ihn (für „In neuem Tab
     * öffnen": der neue Tab erhält die Id über {@link useMusicUploadStore.takeReuseId}, das den
     * URL-Parameter dabei auch entfernt (useWorkingDocUrlState).
     */
    async function reuseById(id: string): Promise<void> {
        await hydrate();
        const entry = entries.value.find((e) => e.id === id);
        if (entry) await reuse(entry);
    }

    // ============ Schritt zurück (Undo entlang der Kette) ============

    /** Findet den aktuell geladenen Stand der laufenden Bearbeitungslinie (über den Zeiger). */
    function resolveCurrentEntry(): HistoryEntry | null {
        const id = useMusicUploadStore().currentEntryId;
        return id ? entries.value.find((e) => e.id === id) ?? null : null;
    }

    /** True, wenn vom aktuellen Stand aus ein vorheriger Stand geladen werden kann. */
    const canLoadPrevious = computed<boolean>(() => {
        const uploadStore = useMusicUploadStore();
        const current = entries.value.find((e) => e.id === uploadStore.currentEntryId) ?? null;
        if (current) return !!current.parentId;
        // Kein Zeiger (z. B. unmittelbar nach Reload): grob anhand der Linie entscheiden.
        const lineage = uploadStore.lineageId;
        return lineage ? entries.value.some((e) => e.sourceFileId === lineage && e.parentId) : false;
    });

    /**
     * Lädt den unmittelbar vorherigen Stand der aktuellen Bearbeitungslinie (Undo). Mehrfaches
     * Aufrufen läuft die Kette via `parentId` bis zum Original zurück. Nutzt
     * {@link useMusicUploadStore.loadFromHistory} (lineage-erhaltend, ohne neuen Eintrag).
     */
    async function loadPreviousState(): Promise<void> {
        const uploadStore = useMusicUploadStore();
        const lineage = uploadStore.lineageId;
        if (!lineage) {
            notificationStore.notify('Keine Datei geladen.', 'info', 1500);
            return;
        }

        // Aktuellen Eintrag bestimmen: Zeiger → sonst per Content-Hash → sonst jüngster der Linie.
        let current = resolveCurrentEntry();
        if (!current) {
            const content = uploadStore.selectedFileContent;
            if (content) {
                const hash = await hashBlob(new Blob([content], {type: MUSICXML_MIME}));
                current =
                    entries.value.find(
                        (e) => e.sourceFileId === lineage && e.contentHash === hash,
                    ) ?? null;
            }
        }
        if (!current) {
            current =
                [...entries.value]
                    .filter((e) => e.sourceFileId === lineage)
                    .sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;
        }

        if (!current?.parentId) {
            notificationStore.notify('Bereits beim Originalstand – nichts zum Zurücksetzen.', 'info', 2000);
            return;
        }

        const parent = entries.value.find((e) => e.id === current!.parentId) ?? null;
        if (!parent) {
            notificationStore.notify('Vorheriger Stand nicht mehr im Verlauf vorhanden.', 'error', 2500);
            return;
        }

        const bytes = await getBlob(parent.id);
        if (!bytes) {
            notificationStore.notify('Vorheriger Stand konnte nicht geladen werden.', 'error', 2500);
            return;
        }

        const file = new File([bytes], parent.filename, {type: parent.mimeType});
        const ok = await uploadStore.loadFromHistory(file, {entryId: parent.id});
        if (ok) notificationStore.notify('Vorheriger Stand geladen.', 'success', 1500);
    }

    /**
     * Stellt den zuletzt bearbeiteten Stand der aktuellen Linie aus der lokalen History wieder her
     * (Reload-Restore). Stateless: der Server kann aus der `file_id` nichts rekonstruieren, daher
     * kommen die Bytes aus IndexedDB. Bevorzugt den Zeiger (`currentEntryId`), sonst den jüngsten
     * Nicht-`converted`-Eintrag der Linie. Gibt `true` zurück, wenn ein Stand geladen wurde.
     */
    async function restoreCurrentFromHistory(): Promise<boolean> {
        const uploadStore = useMusicUploadStore();
        const lineage = uploadStore.lineageId;

        let current = resolveCurrentEntry();
        if (!current && lineage) {
            current =
                [...entries.value]
                    .filter((e) => e.sourceFileId === lineage && e.kind !== 'converted')
                    .sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;
        }
        if (!current) return false;

        const bytes = await getBlob(current.id);
        if (!bytes) return false;

        const file = new File([bytes], current.filename, {type: current.mimeType});
        return uploadStore.loadFromHistory(file, {entryId: current.id});
    }

    // ============ Movement-Dokument (mehrteilige PDFs) ============

    /**
     * Movements (Root-`upload`-Einträge) des aktiven Dokuments, nach ``movementIndex`` sortiert.
     * Grundlage für die Tab-Leiste; leer, wenn kein mehrteiliges Dokument aktiv ist.
     */
    const activeDocumentMovements = computed<HistoryEntry[]>(
        stableBySignature(
            () => {
                const docId = useMusicUploadStore().activeDocumentId;
                return docId
                    ? entries.value
                          .filter((e) => e.documentId === docId && e.kind === 'upload')
                          .sort((a, b) => (a.movementIndex ?? 0) - (b.movementIndex ?? 0))
                    : [];
            },
            (items) => items.map((e) => e.id).join(','),
        ),
    );

    /** Index des aktuell aktiven Movements (über die laufende Lineage ermittelt). */
    const activeMovementIndex = computed<number>(() => {
        const lineage = useMusicUploadStore().lineageId;
        const found = activeDocumentMovements.value.find((e) => e.sourceFileId === lineage);
        return found?.movementIndex ?? 0;
    });

    /**
     * Aktiviert das Movement mit dem angegebenen Index als Arbeitsbasis (Tab-Wechsel). Lädt den
     * jüngsten (Nicht-`converted`-)Stand der Movement-Linie via {@link useMusicUploadStore.loadFromHistory}
     * – ohne neuen Verlaufseintrag/Lineage-Wechsel – und stellt die aktive Linie auf das Movement um.
     */
    async function switchMovement(movementIndex: number): Promise<boolean> {
        const uploadStore = useMusicUploadStore();
        const docId = uploadStore.activeDocumentId;
        if (!docId) return false;

        const root = entries.value.find(
            (e) => e.documentId === docId && e.movementIndex === movementIndex && e.kind === 'upload',
        );
        if (!root) return false;

        // Bereits aktiv? Nichts tun.
        if (uploadStore.lineageId === root.sourceFileId) return true;

        const latest =
            [...entries.value]
                .filter((e) => e.sourceFileId === root.sourceFileId && e.kind !== 'converted')
                .sort((a, b) => b.createdAt - a.createdAt)[0] ?? root;

        const bytes = await getBlob(latest.id);
        if (!bytes) {
            notificationStore.notify('Teil konnte nicht geladen werden.', 'error', 2500);
            return false;
        }

        const file = new File([bytes], latest.filename, {type: latest.mimeType});
        // Griffschrift-Ergebnis des vorherigen Movements verwerfen – es gehört nicht zum neuen
        // Movement (sonst zeigen „Griffschrift"-Tab/DownloadPanel den falschen Stand).
        uploadStore.setConversionResult(null);
        // Aktive Linie auf das Ziel-Movement umstellen (loadFromHistory ändert die lineageId nicht).
        uploadStore.setLineageId(root.sourceFileId);
        return uploadStore.loadFromHistory(file, {entryId: latest.id});
    }

    return {
        // State
        entries,
        isSupported,
        selectedId,
        // Computed
        groups,
        isEmpty,
        selectedEntry,
        canLoadPrevious,
        restoreCurrentFromHistory,
        activeDocumentMovements,
        activeMovementIndex,
        switchMovement,
        // Capture
        addUpload,
        addDerived,
        addConverted,
        attachAnalysis,
        // Lesen
        getText,
        // Detail-Auswahl
        select,
        closeDetail,
        // Management
        hydrate,
        remove,
        rename,
        clearAll,
        reuse,
        download,
        openInNewTab,
        reuseById,
        loadPreviousState,
    } as const;
});
